import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../../core/apiClient';
import './AuthPages.css';

interface VerificationStep {
    id: string;
    title: string;
    description: string;
    status: 'pending' | 'completed';
    action?: React.ReactNode;
}

export const VerifyAccountPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { message, nextSteps, isEnterprise } = location.state || {};

    const [emailCode, setEmailCode] = useState('');
    const [phoneCode, setPhoneCode] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [steps, setSteps] = useState<VerificationStep[]>([
        {
            id: 'email',
            title: 'Email Verification',
            description: 'Verify your email address with the code sent to your inbox',
            status: 'pending'
        },
        {
            id: 'phone',
            title: 'Phone Verification',
            description: 'Verify your phone number with OTP',
            status: 'pending'
        },
        {
            id: 'proof',
            title: isEnterprise ? 'Company Proof' : 'ID Proof',
            description: isEnterprise ? 'Upload GST or Incorporation certificate' : 'Upload National ID or Passport',
            status: 'pending'
        }
    ]);

    const sendEmailVerification = async () => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('access_token');
            const response = await apiClient.post(
                '/api/auth/send-email-verification',
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSuccess(response.data?.message || 'Verification code sent');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to send verification code');
        } finally {
            setLoading(false);
        }
    };

    const verifyEmail = async () => {
        if (!emailCode) {
            setError('Please enter the verification code');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('access_token');
            const response = await apiClient.post(
                '/api/auth/verify-email',
                { code: emailCode },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSuccess(response.data?.message || 'Email verified successfully');
            updateStepStatus('email', 'completed');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Email verification failed');
        } finally {
            setLoading(false);
        }
    };

    const sendPhoneVerification = async () => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('access_token');
            const response = await apiClient.post(
                '/api/auth/send-phone-verification',
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSuccess(response.data?.message || 'OTP sent');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to send OTP');
        } finally {
            setLoading(false);
        }
    };

    const verifyPhone = async () => {
        if (!phoneCode) {
            setError('Please enter the OTP');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('access_token');
            const response = await apiClient.post(
                '/api/auth/verify-phone',
                { code: phoneCode },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSuccess(response.data?.message || 'Phone verified successfully');
            updateStepStatus('phone', 'completed');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Phone verification failed');
        } finally {
            setLoading(false);
        }
    };

    const uploadProof = async () => {
        if (!file) {
            setError('Please select a file to upload');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('access_token');
            const formData = new FormData();
            formData.append('file', file);

            // Step 1: Upload file to get file_url
            const uploadRes = await apiClient.post(
                '/api/upload',
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            const fileUrl = uploadRes.data?.data?.file_url || uploadRes.data?.file_url;
            if (!fileUrl) {
                throw new Error('Failed to get file URL from upload');
            }

            // Step 2: Send file_url to verify-proof endpoint
            const response = await apiClient.post(
                '/api/auth/upload-proof',
                { file_url: fileUrl },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            setSuccess(response.data?.message || 'Proof uploaded successfully');
            updateStepStatus('proof', 'completed');
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Upload failed');
        } finally {
            setLoading(false);
        }
    };

    const updateStepStatus = (stepId: string, status: 'pending' | 'completed') => {
        setSteps(prev =>
            prev.map(step =>
                step.id === stepId ? { ...step, status } : step
            )
        );
    };

    return (
        <div className="auth-container">
            <div className="auth-card">
                <div className="auth-header">
                    <h1>Verify Your Account</h1>
                    <p>{message || 'Complete the verification steps below'}</p>
                </div>

                {error && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="alert alert-success">
                        {success}
                    </div>
                )}

                <div className="verification-steps">
                    {/* Email Verification */}
                    <div className={`verification-step ${steps[0].status === 'completed' ? 'completed' : 'active'}`}>
                        <div className="step-header">
                            <div className="step-title">1. Email Verification</div>
                            <span className={`step-status ${steps[0].status}`}>
                                {steps[0].status === 'completed' ? '✓ Completed' : 'Step 1'}
                            </span>
                        </div>
                        <div className="step-content">
                            <p>Verify your email address with the code sent to your inbox</p>
                            {steps[0].status === 'pending' && (
                                <div className="step-actions">
                                    <button
                                        onClick={sendEmailVerification}
                                        className="btn btn-secondary btn-sm"
                                        disabled={loading}
                                    >
                                        {loading ? 'Sending...' : 'Send Code'}
                                    </button>
                                    <div className="code-input-group">
                                        <input
                                            type="text"
                                            placeholder="6-digit code"
                                            value={emailCode}
                                            onChange={(e) => setEmailCode(e.target.value)}
                                            maxLength={6}
                                        />
                                        <button
                                            onClick={verifyEmail}
                                            className="btn btn-primary btn-sm"
                                            disabled={loading || emailCode.length < 6}
                                        >
                                            Verify
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Phone Verification */}
                    <div className={`verification-step ${steps[1].status === 'completed' ? 'completed' : (steps[0].status === 'completed' ? 'active' : 'disabled')}`}>
                        <div className="step-header">
                            <div className="step-title">2. Phone Verification</div>
                            <span className={`step-status ${steps[1].status === 'completed' ? 'completed' : (steps[0].status === 'completed' ? 'active' : 'pending')}`}>
                                {steps[1].status === 'completed' ? '✓ Completed' : 'Step 2'}
                            </span>
                        </div>
                        <div className="step-content">
                            <p>Verify your phone number with OTP</p>
                            {steps[1].status === 'pending' && steps[0].status === 'completed' && (
                                <div className="step-actions">
                                    <button
                                        onClick={sendPhoneVerification}
                                        className="btn btn-secondary btn-sm"
                                        disabled={loading}
                                    >
                                        {loading ? 'Sending OTP...' : 'Send OTP'}
                                    </button>
                                    <div className="code-input-group">
                                        <input
                                            type="text"
                                            placeholder="6-digit OTP"
                                            value={phoneCode}
                                            onChange={(e) => setPhoneCode(e.target.value)}
                                            maxLength={6}
                                        />
                                        <button
                                            onClick={verifyPhone}
                                            className="btn btn-primary btn-sm"
                                            disabled={loading || phoneCode.length < 6}
                                        >
                                            Verify
                                        </button>
                                    </div>
                                </div>
                            )}
                            {steps[0].status !== 'completed' && <p className="step-locked">Complete email verification first</p>}
                        </div>
                    </div>

                    {/* Proof Upload */}
                    <div className={`verification-step ${steps[2].status === 'completed' ? 'completed' : (steps[1].status === 'completed' ? 'active' : 'disabled')}`}>
                        <div className="step-header">
                            <div className="step-title">3. {isEnterprise ? 'Company Proof' : 'ID Proof'}</div>
                            <span className={`step-status ${steps[2].status === 'completed' ? 'completed' : (steps[1].status === 'completed' ? 'active' : 'pending')}`}>
                                {steps[2].status === 'completed' ? '✓ Completed' : 'Step 3'}
                            </span>
                        </div>
                        <div className="step-content">
                            <p>{isEnterprise ? 'Upload GST or Incorporation certificate' : 'Upload National ID or Passport'}</p>
                            {steps[2].status === 'pending' && steps[1].status === 'completed' && (
                                <div className="file-upload-group" style={{ marginTop: '1rem' }}>
                                    <input
                                        type="file"
                                        onChange={(e) => setFile(e.target.files ? e.target.files[0] : null)}
                                        accept="image/*,.pdf"
                                        className="file-input"
                                    />
                                    <button
                                        onClick={uploadProof}
                                        className="btn btn-primary btn-block"
                                        disabled={loading || !file}
                                        style={{ marginTop: '1rem' }}
                                    >
                                        {loading ? 'Uploading...' : 'Upload Document'}
                                    </button>
                                </div>
                            )}
                            {steps[1].status !== 'completed' && <p className="step-locked">Complete phone verification first</p>}
                        </div>
                    </div>

                    {/* Completion Message */}
                    {steps.every(step => step.status === 'completed') && (
                        <div className="verification-complete" style={{ textAlign: 'center', marginTop: '2rem' }}>
                            <h3>Verification Steps Completed!</h3>
                            <p>Your email, phone, and documents have been verified.</p>
                            <button 
                                onClick={() => navigate(isEnterprise ? '/enterprise/dashboard' : '/user/balance')}
                                className="btn btn-primary"
                                style={{ marginTop: '1rem' }}
                            >
                                Go to Dashboard
                            </button>
                        </div>
                    )}
                </div>

                <div className="auth-footer" style={{ marginTop: '2rem' }}>
                    <a href="/login">Back to Login</a>
                </div>
            </div>
        </div>
    );
};

export default VerifyAccountPage;
